import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { ThunkDispatch } from 'redux-thunk';
import { History } from 'history';
import { Actions } from '../store/actions';
import { CreateVisitAction, initVisit } from '../store/actions/visit';
import {
    getAllVisitsByPatientLocalId,
    getDForm,
    getDoc,
    getDocIds,
    getMe,
    getPatientByLocalId,
    getSelectedClinicId,
    getSingleDoctor,
} from '../store/idbHandler';
import { GlobalState } from '../store/reducers';
import { Patient } from '../store/types/patient';
import { GO_TO_PRESCRIPTION_PAD } from '../routes/paths';
import { track } from '../tracking';
import moment from 'moment';
import {
    LAB_DOCTOR_PACKAGE_PREFIX,
    LOCAL_ID_PREFIX,
    LAB_VENDOR_PACKAGE_PREFIX,
    DYNAMIC_FORMS,
} from '../constants';
import { Visit } from '../store/types/visit';
import fetchMedicalRecords, { MedicalRecordsResponse, NO_ACCESS } from '../api/fetchMedicalRecords';
import { UIDocumentEntity } from './MedicalRecordsRightPanel';
import deleteMedicalRecord from '../api/deleteMedicalRecord';
import fetchAllVisitsForPatient, { VISIT_TYPE } from '../api/fetchAllVisitsForPatient';
import { createDBTool, randomString, uniqueBy } from '../store/utils';
import { PatientDetailsVisits } from '../store/reducers/prescriptionPad';
import { MedicationsEntity } from '../store/types/prescription';
import { DOCS_APP_BE_URL, PATIENT_PROFILE_CLOUDFRONT, RX_PDF_HOST } from '../api/constants';
import { notification } from 'antd';
import { captureError } from './captureError';
import pushPatientAvatar from '../api/pushPatientAvatar';
import {
    DrugsLabTestsStatusMap,
    ItemType,
} from '../components/prescriptionPad/rightPanel/workspace/PrescribeAutoSuggestV2';
import fetchDrugsAndLabTestsStatus from '../api/fetchDrugsAndLabTestsStatus';
import capitalize from 'lodash/capitalize';
import { DFormEntity } from '../api/fetchDoctorGenieByOID';
import { SectionNameConfig } from '../store/db';
import { GROWTH_CHART_TYPE } from '../components/prescriptionPad/rightPanel/workspace/padV2/growthChart/constants/growthChartType';
import { FETCH_STATE } from '../components/common/PatientOverviewUtils';
import { LabTest } from '../api/fetchMatchingDrugsLabTests';
import { PatientRes } from '../api/fetchPtGlobal';
import { SALUTATION } from '../components/common/constants/add-patient';

export interface Encounter {
    id: string;
    period_start: number;
    period_end: number;
    discharge_initiated: boolean;
    facility: string;
    class: string;
    practitioner_name: string;
    practitioner_id: string;
}

export const capitalizeString = (value: string): string => {
    return capitalize(value);
};

export const onCreateVisit = ({
    patient,
    docid,
    history,
    dispatch,
    visitId,
    cId,
    meta,
}: {
    patient?: Patient;
    docid: string;
    history: History;
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    visitId?: string;
    cId?: string;
    meta?: Partial<CreateVisitAction['meta']>;
}): void => {
    if (!patient) {
        return;
    }

    const onSuccess = (prescriptionId: string) => {
        history.push({
            pathname: GO_TO_PRESCRIPTION_PAD(prescriptionId, 'edit'),
        });
    };
    const { personal, localId, id, relations } = patient;
    const { name, age, gender, phone } = personal;
    const initVisitAction = initVisit(
        {
            localId: localId,
            id: id,
            uuid: patient.uuid,
            name: name.f + ' ' + (name.l ? name.l : ''),
            phCode: phone?.c,
            phoneNumber: phone?.n,
            age: age,
            gender: gender?.toString() || '',
            onApp: personal.onApp,
            formData: patient.formData,
            'health-ids': patient.personal['health-ids'],
            abha: Array.isArray(patient.abhaV2)
                ? patient.abhaV2?.find((abha) => abha.cId === cId || getSelectedClinicId() || '')
                : patient?.abha,
            relations,
        },
        docid,
        cId || getSelectedClinicId() || '',
        visitId,
        onSuccess,
        undefined,
        { autoCopy: true, ...meta },
    );
    track('Start_Visit', { from: 'Pt_Details' });
    initVisitAction && dispatch(initVisitAction);
};

export const lead0 = (n: number): string => {
    return n < 10 ? `0${n}` : `${n}`;
};

export const isIPad = (): boolean => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.userAgent?.isiPad || (isApple && navigator.maxTouchPoints > 1)
    );
};

const isApple = [
    'iPhone',
    'iPad',
    'iPod',
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
].includes(navigator.platform);

export const getColorForString = (stringInput?: string): string => {
    let hash = 0;
    if (!stringInput || stringInput.length === 0) return '#000';
    for (let i = 0; i < stringInput.length; i++) {
        hash = stringInput.charCodeAt(i) + ((hash << 5) - hash);
        hash = hash & hash;
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 255;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

export function getFirstAndLastCharName(
    name: string,
): {
    firstNameChar: string;
    lastNameChar: string | null;
} {
    const breakName = name.split(' ');
    const breakNameLength = breakName.length;

    if (breakNameLength === 1) {
        return {
            firstNameChar: breakName[0][0],
            lastNameChar: null,
        };
    }

    return {
        firstNameChar: breakName[0][0],
        lastNameChar: breakName[breakNameLength - 1][0],
    };
}

export const addAlpha = (color: string, opacity: number): string => {
    // coerce values so ti is between 0 and 1.
    const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
    return color + _opacity.toString(16).toUpperCase();
};

export const getDayWord = (
    date: string,
    elseFormat: string | null = 'dddd',
    suffix: string = '',
): string => {
    const md = moment(date);
    const today = moment();

    if (md.isSame(today, 'date')) {
        return `Today${suffix}`;
    }

    const tomorrow = moment().add(1, 'days');
    const yestarday = moment().add(-1, 'days');
    if (md.isSame(tomorrow, 'date')) {
        return `Tomorrow${suffix}`;
    }
    if (md.isSame(yestarday, 'date')) {
        return `Yesterday${suffix}`;
    }
    if (elseFormat === null) {
        return '';
    }
    return md.format(elseFormat);
};

export function capitalizeFirstLetter(text: string): string {
    return text?.charAt(0).toUpperCase() + text?.slice(1);
}

export const getPxPrintUrl = (pxId: string): string => {
    const fileName = `${pxId}-review-print.pdf?v=${new Date().getTime()}`;
    return `${RX_PDF_HOST}/${fileName}`;
};

export const getPxSendUrl = (pxId: string): string => {
    const fileName = `${pxId}.pdf?v=${new Date().getTime()}`;
    return `${RX_PDF_HOST}/${fileName}`;
};

export const isLocalItem = (id: string): boolean => {
    return id.startsWith(LOCAL_ID_PREFIX);
};

export const getAllVisitsWithLinkedPatientIds = async (id: string): Promise<Visit[]> => {
    const data = await getAllPtIds(id);
    const allPtIds = data?.allPtIds;
    const docData = getDoc();
    const name = docData?.profile.personal.name;
    const doc_name = [name?.f, name?.l].filter(Boolean).join(' ');

    if (!allPtIds) {
        return [];
    }
    let allVisits = (await getAllVisitsByPatientLocalId(...allPtIds)) || [];
    allVisits = allVisits.map((i) => {
        if (i.type) return i;
        return {
            ...i,
            type: VISIT_TYPE.SELF,
            docname: doc_name,
        };
    });

    return allVisits;
};

export const getAllVisitsWithPatientIds = async (ids: string[]): Promise<Visit[]> => {
    const docData = getDoc();
    const name = docData?.profile.personal.name;
    const doc_name = [name?.f, name?.l].filter(Boolean).join(' ');

    if (!ids.length) {
        return [];
    }

    let allVisits = (await getAllVisitsByPatientLocalId(...ids)) || [];

    allVisits = allVisits.map((i) => {
        if (i.type) return i;
        return {
            ...i,
            type: VISIT_TYPE.SELF,
            docname: doc_name,
        };
    });

    return allVisits;
};

export const getAllPtIds = async (
    id: string,
): Promise<{ allPtIds: string[]; patient: Patient } | undefined> => {
    const patient = await getPatientByLocalId(id);

    if (!patient) {
        return;
    }

    const allPtIds = (patient.link || []).concat(id);

    return { allPtIds, patient };
};

export const fetchMR = async (
    plId: string,
    search_term?: string,
    fetchWithRefresh?: boolean,
    limit?: boolean,
): Promise<
    | {
          fetchState: 'error' | 'success' | typeof NO_ACCESS;
          medicalRecords?: UIDocumentEntity[];
          patient: Patient;
          allPtIds: string[];
          poll?: boolean;
          sourceRefreshedAt?: number;
          encounters?: Encounter[];
      }
    | undefined
> => {
    const data = await getAllPtIds(plId);

    const allPtIds = data?.allPtIds;

    if (!data || !allPtIds) {
        return;
    }

    const allFetch = allPtIds.map((id) =>
        fetchMedicalRecords(id, search_term, fetchWithRefresh && id === plId ? true : false, limit),
    );

    try {
        let poll;
        let sourceRefreshedAt;
        const allRes = await Promise.allSettled(allFetch);
        const temp: (UIDocumentEntity[] | undefined | null)[] = allRes
            .map((t, index) => {
                const result = t as PromiseFulfilledResult<MedicalRecordsResponse>;
                if (allPtIds[index] === plId) {
                    if (result?.value?.poll) {
                        poll = true;
                    }
                    sourceRefreshedAt = result?.value?.source_refreshed_at;
                }
                return t.status === 'fulfilled'
                    ? result.value.documents?.map((item) => ({ ...item, _pt_oid: allPtIds[index] }))
                    : undefined;
            })
            .filter(Boolean);

        const tempEncounters: (Encounter[] | undefined)[] = allRes
            .map((t) => {
                const result = t as PromiseFulfilledResult<MedicalRecordsResponse>;
                return t.status === 'fulfilled' ? result?.value?.encounters : undefined;
            })
            .filter(Boolean);
        const allEncounters = ([] as Encounter[]).concat(...(tempEncounters as Encounter[][]));

        const allDocs = ([] as UIDocumentEntity[])
            .concat(...(temp as UIDocumentEntity[][]))
            .sort((a, b) => new Date(b.u_at).getTime() - new Date(a.u_at).getTime());

        const isAllPromisesFailed = allRes.every((p) => p.status === 'rejected');

        return {
            medicalRecords: uniqueBy('document_id', allDocs),
            fetchState: isAllPromisesFailed ? 'error' : 'success',
            patient: data?.patient,
            allPtIds: data.allPtIds,
            poll: poll,
            sourceRefreshedAt: sourceRefreshedAt,
            encounters: uniqueBy('id', allEncounters),
        };
    } catch (e) {
        if ((e as Error).message === NO_ACCESS) {
            return {
                fetchState: NO_ACCESS,
                patient: data.patient,
                allPtIds: data.allPtIds,
            };
        }

        console.error('Error fetchMedicalRecords', e);

        return {
            fetchState: 'error',
            patient: data.patient,
            allPtIds: data.allPtIds,
        };
    }
};

export const fetchAllVisits = async (
    plId: string,
    limit: number = 0,
): Promise<PatientDetailsVisits | undefined> => {
    try {
        const allVisitPt = await fetchAllVisitsForPatient(plId, limit);

        if (!allVisitPt) {
            return;
        }

        if (allVisitPt.success && allVisitPt.data) {
            const visits = {
                state: 'success',
                data: allVisitPt.data.map((t) => ({
                    ...createDBTool(t),
                    date: t.date,
                    visitId: t.visitId,
                    precriptionId: t.id,
                    flavour: t.flavour,
                    translateLang: t.translateLang,
                    patient: t.patient,
                    type: t.type,
                    doc_name: t.doc_name,
                })),
            };

            return visits as PatientDetailsVisits;
        } else {
            track('ERROR', { on: 'fetchAllVisits', error: allVisitPt.error });
            return {
                state: FETCH_STATE.ERROR,
            };
        }
    } catch (e) {
        track('ERROR', { on: 'fetchAllVisits', error: (e as Error).message });
        return {
            state: FETCH_STATE.ERROR,
        };
    }
};

export const getOrdinalSufics = (count: number): string => {
    return {
        one: 'st',
        two: 'nd',
        few: 'rd',
        other: 'th',
        many: '',
        zero: '',
    }[
        new Intl.PluralRules('en-IN', {
            type: 'ordinal',
        }).select(count)
    ];
};

export const onMedicalRecordDelete = async (documentId: string, plId: string): Promise<void> => {
    if (!documentId) {
        return;
    }
    if (window.confirm('Are you sure, you want to delete this record')) {
        try {
            await deleteMedicalRecord(documentId, plId);
        } catch (e) {
            console.log('delete record error', e);
            track('ERROR', { on: 'onMedicalRecordDelete', error: (e as Error).message });
            throw e;
        }
    } else {
        return;
    }
};

export const LS_NEW_PT_DF_AS = 'LS_NEW_PT_DF_AS';

export const dumpDynamicFormAutoSuggest = async (patients: Patient[]): Promise<void> => {
    if (!patients.length) {
        return;
    }

    const baclkistedKeys = ['salutation', 'uhid'];

    const obj: { [k: string]: Set<string | number | { [key: string]: string }> } = {};
    const referred_by_name_and_number = 'referred-by-name-and-numberv3';

    const trackIsReferredByNameNumberAlreadyExists: { [key: string]: boolean } = {};

    obj[referred_by_name_and_number] = new Set([]);

    const data: { [k: string]: (string | number | { [key: string]: string })[] } = JSON.parse(
        localStorage.getItem(LS_NEW_PT_DF_AS) || '{}',
    );

    const oldReferralData: { name: string; number: string }[] = (data[
        referred_by_name_and_number
    ] || []) as { name: string; number: string }[];
    oldReferralData.forEach((item) => {
        trackIsReferredByNameNumberAlreadyExists[
            `${item.name?.toLowerCase()}#${item.number}`
        ] = true;
    });

    patients.forEach((pt) => {
        pt.formData?.forEach((item) => {
            const iKey = item.key.toLowerCase();
            if (baclkistedKeys.indexOf(iKey) !== -1) {
                return;
            }
            if (obj[iKey]) {
                obj[iKey].add(item.value);
            } else {
                obj[iKey] = new Set([item.value]);
            }
        });

        const name = String(
            (pt?.formData?.find((form) => form?.key === 'referredby')?.value || '') as string,
        ).trim();
        const number = String(
            pt?.formData?.find((form) => form?.key === 'referredbynumber')?.value
                ? `${pt?.formData?.find((form) => form?.key === 'referredbynumber')?.value}`
                : '',
        ).trim();

        if (!name && !number) {
            return;
        }

        if (!trackIsReferredByNameNumberAlreadyExists[`${name.toLowerCase()}#${number}`]) {
            obj[referred_by_name_and_number].add({
                name: name,
                number: number,
            });
            trackIsReferredByNameNumberAlreadyExists[`${name.toLowerCase()}#${number}`] = true;
        }
    });

    Object.keys(obj).forEach((t) => {
        data[t] = Array.from(new Set([...Array.from(new Set(data[t])), ...Array.from(obj[t])]));
    });

    try {
        if (data['referred-by-name-and-number']) {
            delete data['referred-by-name-and-number'];
        }
    } catch (e) {}

    localStorage.setItem(LS_NEW_PT_DF_AS, JSON.stringify(data));
};

export function detectTouchscreen(): boolean {
    let result = false;
    if (window.PointerEvent && 'maxTouchPoints' in navigator) {
        // if Pointer Events are supported, just check maxTouchPoints
        if (navigator.maxTouchPoints > 0) {
            result = true;
        }
    } else {
        // no Pointer Events...
        if (window.matchMedia && window.matchMedia('(any-pointer:coarse)').matches) {
            // check for any-pointer:coarse which mostly means touchscreen
            result = true;
        } else if (window.TouchEvent || 'ontouchstart' in window) {
            // last resort - check for exposed touch events API / event handler
            result = true;
        }
    }
    return result;
}

export const getDrugAllergy = (allergies: Set<string>, med: MedicationsEntity): boolean => {
    return (allergies?.has(med.id) ||
        allergies?.has(med.name?.toLowerCase()) ||
        (med.generic_id && allergies?.has(med.generic_id)) ||
        (med.generic_name && allergies?.has(med.generic_name.toLowerCase()))) as boolean;
};

export const checkIfValidPhoneNumber = (
    phCode: string,
    phoneNumber?: string,
    countryCodeEnum?: CountryCode,
): boolean => {
    if (!phoneNumber) {
        return false;
    }

    if (!phCode || phCode === '+91') {
        return /^[6-9]\d{9}$/gi.test(phoneNumber || '') && !/(\d)\1{9}/g.test(phoneNumber || '');
    }

    return isValidPhoneNumber((phCode + phoneNumber).trim(), countryCodeEnum);
};

export const uploadPatientPic = async (pid: string, pic?: File): Promise<void> => {
    try {
        if (!pic) {
            return;
        }
        const formData = new FormData();
        formData.append('patient-pic', pic);
        if (pid) {
            notification.success({
                message:
                    'Profile pic updated successfully. It may take a few minutes to reflect the changes.',
                placement: 'topRight',
                duration: 5,
            });
            await pushPatientAvatar(formData, pid);
        } else {
            notification.error({
                message: 'Profile pic upload failed.',
                placement: 'topRight',
                duration: 5,
            });
            throw new Error('Unable to push patient pic');
        }
    } catch (error) {
        notification.error({
            message: 'Profile pic upload failed.',
            placement: 'topRight',
            duration: 5,
        });
        captureError(error as Error);
        track('ERROR', { on: 'EditPatientDetailsDialog', what: (error as Error).message });
        console.error(error);
    }
};

export const getPatientPicUrl = (pid?: string): string | undefined => {
    if (pid) {
        return `${PATIENT_PROFILE_CLOUDFRONT}/user-avatar/${pid}?cacheoff=${randomString(4)}`;
    }

    return;
};

export const fetchDrugsLabTestsStatus = async (
    drugs: ItemType[],
    labs: ItemType[],
    fetchHXNGStatuses: boolean = false,
): Promise<DrugsLabTestsStatusMap> => {
    if (!isOnboardedForPharma() && !isOnboardedForLab() && !fetchHXNGStatuses) return {};

    const drugIds = drugs.map((i) => i.id);
    const labIds = labs.filter((i) => !i.id.startsWith(LOCAL_ID_PREFIX)).map((i) => i.id);
    const requestPayload = {
        drugs: {
            ids: drugIds,
        },
        labs: {
            ids: labIds,
        },
    };

    if (!drugIds.length && !labIds.length) return {};

    try {
        const { status = {} } = await fetchDrugsAndLabTestsStatus(requestPayload);
        const { drugs = {}, labs = {} } = status;
        return { ...drugs, ...labs };
    } catch (err) {
        track('ERROR', {
            on: 'fetchDrugsLabTestsStatus',
            error: (err as Error).message,
        });
        return {};
    }
};

export function getTopSuggestionsForLabs(options: ItemType[]): ItemType[] {
    let testCount = 0;
    let packageCount = 0;
    const newOptions: ItemType[] = [];

    for (let i = 0; i < options.length; i++) {
        const item = options[i];
        if (isHXNGVendorPackageOrDoctorPackage(item)) {
            if (packageCount < 7) {
                newOptions.push(item);
                packageCount++;
            }
        } else {
            if (testCount < 7) {
                newOptions.push(item);
                testCount++;
            }
        }
        if (packageCount === 7 && testCount === 7) break;
    }
    return newOptions;
}

export function getTestsAndPackagesFromOptions(options: ItemType[]): { [k: string]: ItemType[] } {
    const tests = options.filter((i) => !isHXNGVendorPackageOrDoctorPackage(i));
    const packages = options.filter((i) => isHXNGVendorPackageOrDoctorPackage(i));
    tests.splice(8);
    packages.splice(8);
    return { tests, packages };
}

export function isHXNGVendorPackageOrDoctorPackage(item: LabTest): boolean {
    const { id, hxng_only } = item;
    const isForHxngOnly = hxng_only === undefined || hxng_only === true;
    return (
        isForHxngOnly &&
        (id.startsWith(LAB_VENDOR_PACKAGE_PREFIX) || id.startsWith(LAB_DOCTOR_PACKAGE_PREFIX))
    );
}

export const isDoctor = (): boolean =>
    getDoc()?.loginmeta?.role === 'DOCTOR' &&
    getMe()?.doctor !== null &&
    getMe()?.doctor !== undefined;

export const HXNG = 'HXNG'; // both pharma and lab
export const HELB = 'HELB'; // only lab

/**
 * isOnboardedForPharma, currently it is hardcoded to false, but when we change we need to do same changes that we did for labs =>
 * - @see isOnboardedForLab func
 */
export const isOnboardedForPharma = (): boolean => {
    // return Boolean(getDoc()?.profile?.professional?.onboarded_for?.includes(HXNG));
    return false;
};

export const isOnboardedForLab = (): boolean => {
    return Boolean(
        getSingleDoctor()?.profile?.professional?.onboarded_for?.includes(HXNG) ||
            getSingleDoctor()?.profile?.professional?.onboarded_for?.includes(HELB),
    );
};

export const hxngMonetisation = 'hxng-monetisation';

export const isRXMT1 = 'isRXMT1';
export const isRXMT2 = 'isRXMT2';
export const isRXMT3 = 'isRXMT3';
export const isRXMT4 = 'isRXMT4';

export const tooltipEnabledMonetization = new Set([isRXMT1, isRXMT2]);
export const checkboxEnabledMonetization = new Set([isRXMT3, isRXMT4]);

export type MONETISATION_TYPE = typeof isRXMT3 | typeof isRXMT4;

export const isRXMonetisationT1 = (): boolean => {
    return Boolean(getDoc()?.profile?.professional?.[hxngMonetisation] === isRXMT1);
};

export const isRXMonetisationT2 = (): boolean => {
    return Boolean(getDoc()?.profile?.professional?.[hxngMonetisation] === isRXMT2);
};

export const isRXMonetisationT3 = (): boolean => {
    return Boolean(getDoc()?.profile?.professional?.[hxngMonetisation] === isRXMT3);
};

export const isRXMonetisationT4 = (): boolean => {
    return Boolean(getDoc()?.profile?.professional?.[hxngMonetisation] === isRXMT4);
};

export type HxngMonetisation = typeof isRXMT1 | typeof isRXMT2 | typeof isRXMT3 | typeof isRXMT4;

export const getRXMonetisationType = (): HxngMonetisation | undefined => {
    const rxMonetisationType = getDoc()?.profile?.professional?.[hxngMonetisation];
    return rxMonetisationType;
};

export const isForRxMonetisation = (): MONETISATION_TYPE => {
    const rxMonetisationType = getRXMonetisationType() || '';
    const sendRxMonetizationMessage = checkboxEnabledMonetization.has(rxMonetisationType);
    const isForRxMonetisation = sendRxMonetizationMessage ? rxMonetisationType : '';
    return isForRxMonetisation as MONETISATION_TYPE;
};

export const openCrispChat = (
    trackingName: string,
    trackingObject: { [k: string]: string | undefined } = {},
    text: string,
): void => {
    try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        $crisp.push(['do', 'message:send', ['text', text]]);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        $crisp.push(['do', 'chat:open']);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        $crisp.push(['do', 'chat:show']);
        track(trackingName, trackingObject);
    } catch (err) {
        captureError(err as Error);
        track('ERROR', {
            on: trackingName,
            what: (err as Error).message,
        });
    }
};

export const getAddPDForms = async (): Promise<DFormEntity[]> => {
    const dForms = await getDForm('addp');

    const isReferredByPresent = dForms.find((dForm) => dForm.key === 'referredby');

    if (isReferredByPresent) {
        const referredByNumber = DYNAMIC_FORMS.find((dForm) => dForm.key === 'referredbynumber');

        if (referredByNumber) {
            const data = uniqueBy('key', [...dForms, referredByNumber]);
            return data;
        } else {
            const data = uniqueBy('key', dForms);
            return data;
        }
    } else {
        const data = dForms.filter((dForm) => dForm.key !== 'referredbynumber');
        return data;
    }
};

export const getSectionNameFromKey = (
    key: string,
    sectionNameConfig?: SectionNameConfig,
): string | undefined => {
    if (key === 'patientMedicalHistory') {
        return sectionNameConfig?.['medicalHistory'];
    }

    if (key === 'lab-investigations') {
        return sectionNameConfig?.['labTests'];
    }

    if (key === 'examination-findings') {
        return sectionNameConfig?.['examinations'];
    }

    if (key === 'followup-advices') {
        return `${sectionNameConfig?.['followup'] || 'Follow Up'} & ${
            sectionNameConfig?.['advices'] || 'Advices'
        }`;
    }

    return sectionNameConfig?.[key];
};

export const roundoffNumberIfDecimal = (num: number) => {
    const toDisplay = Number.isInteger(num) ? num : num.toFixed(2);
    return toDisplay;
};

export const fetchAndStoreGrowthChartData = async () => {
    try {
        if (Object.keys(window?.growthChartData || [])?.length) {
            return window.growthChartData;
        }
        const res = await fetch('/growth_chart_constants.json?v=1');
        const data = await res.json();
        window.growthChartData = data as GROWTH_CHART_TYPE;
        if (Object.keys(window?.growthChartData || [])?.length) {
            return window.growthChartData;
        }
    } catch (error) {
        captureError(error as Error);
        track('ERROR', { on: 'fetchAndStoreGrowthChartData', error: (error as Error).message });
        return undefined;
    }
};

export const utf8ToBase64 = (str: string): string => {
    // Encode the string as UTF-8
    const utf8Bytes = new TextEncoder().encode(str);

    // Convert the byte array to a string
    let binaryString = '';
    for (let i = 0; i < utf8Bytes.length; i++) {
        binaryString += String.fromCharCode(utf8Bytes[i]);
    }

    // Encode the binary string to Base64
    return btoa(binaryString);
};

export const parseDoctorName = (docName?: string) => {
    const [_, fn, ln] = docName?.split(' ') || [];
    return [fn?.[0], ln?.[0]].filter(Boolean).join('');
};

export const isBusinessVisit = (visit?: { type?: string }) => {
    return visit?.type === VISIT_TYPE.BUSINESS;
};
export const isEditableVisitForMe = (visit?: { docid?: string }) => {
    const isEditable = getDocIds().find((docId: string) => docId === visit?.docid);
    return isEditable;
};

export const isSelfVisit = (visit?: { type?: string }) => {
    return visit?.type === VISIT_TYPE.SELF;
};

export const isMrVisit = (visit?: { type?: string }) => {
    return visit?.type === VISIT_TYPE.MR;
};

export const formattedNumber = (number: number): string => number.toLocaleString('en-IN');

export const queryToMap = (queryString: string): { [k: string]: string } => {
    const params = new URLSearchParams(queryString);
    const result: { [k: string]: string } = {};
    for (const [key, value] of params.entries()) {
        result[key] = value;
    }
    return result;
};

export function getEncounterColors(type: string): { color: string; bg: string } {
    switch (type) {
        case 'IP':
            return { color: '#19A66A', bg: '#D1EDE1' };
        case 'OP':
            return { color: '#7A9FFF', bg: '#E9EFFF' };
        case 'EM':
            return { color: '#E7938D', bg: '#FBEDEC' };
        case 'DC':
            return { color: '#EDAE0C', bg: '#FDF7E7' };
        default:
            return { color: '#7A9FFF', bg: '#E9EFFF' };
    }
}

export const toLocalPt = (pt: PatientRes[]): Patient[] => {
    return pt.map((pt) => {
        const { oid, fln, mobile, username } = pt;
        return {
            id: oid,
            localId: oid,
            personal: {
                name: {
                    f: fln,
                    l: '',
                },
                phone: mobile
                    ? {
                          c: '+91',
                          n: mobile,
                      }
                    : undefined,
                gender: '',
            },
            formData: username
                ? [
                      {
                          label: 'UHID',
                          type: 'string',
                          key: 'uhid',
                          value: username,
                      },
                  ]
                : undefined,
        };
    });
};

export const sanitizeSalutation = (sal: typeof SALUTATION[number]) => {
    return sal ? sal?.toString()?.replace('.', '') : '';
};

export const getSalutation = (formData?: Patient['formData']) => {
    const salutation = formData?.find((data) => data.key === 'salutation')?.value;
    return salutation ? `${salutation} ` : '';
};
